<template>
  <!--   All these pixel counts subtract the headers, the tabs, and top padding so that the page will try
         to fit it all within the page as best it can  -->
  <div style="height: calc(100vh - 40px - 24px - 34px)">
    <TrustedContactsTable
      :additional-columns="settingsColumn"
      :worker-panel-active="detailsSidebarVisible"
      @filter-drawer-toggled="filterDrawerToggled"
      @row-click="openDetails"
      @tab-toggled="toggleActiveTab"
    >
      <!-- eslint-disable-next-line vue/no-unused-vars-->
      <template #title="{ contactsCount, refetch }">
        <PageHeader>
          <template #header-title>{{ contactsCount ?? '' }} {{ $t('Trusted contacts') }}</template>
          <template #header-actions>
            <FeatureFlagged name="applied-contacts-page">
              <a
                v-cloak
                :href="laravelNamedRoute(namedRoutes.contacts.applied)"
                class="tw-flex tw-flex-row tw-gap-2 tw-items-center tw-border tw-border-solid tw-border-gray-300 tw-rounded-2xl tw-px-3 tw-py-1"
              >
                <span class="tw-font-medium tw-text-lg tw-text-gray-800">
                  {{ $t('Requests to join') }}
                </span>
                <span
                  class="tw-font-bold tw-bg-red-600 tw-text-sm tw-text-white tw-rounded-2xl tw-px-2 tw-py-0.5"
                >
                  {{ totalRequestsToJoin }}
                </span>
              </a>
            </FeatureFlagged>
            <WdsMenu :z-index="21" placement="bottom-end">
              <template #trigger>
                <WdsButtonGhost data-test="option-menu">
                  <WdsIcon name="overflow-menu-horizontal" class="tw-cursor-pointer" />
                </WdsButtonGhost>
              </template>
              <WdsMenuOption data-test="invite-link" @click="openInviteLinkModal">
                <WdsIcon :size="16" name="link" />
                {{ $t('Get invite link') }}
              </WdsMenuOption>
              <WdsMenuOption data-test="bulk-import" @click="openImportModal">
                <WdsIcon :size="16" name="user-profile" />
                {{ $t('Import contacts in bulk') }}
              </WdsMenuOption>
            </WdsMenu>

            <WdsButton primary data-test="invite-button" @click="toggleInviteModal">
              {{ $t('Invite') }}
            </WdsButton>
          </template>
        </PageHeader>
      </template>

      <template #Settings="{ row, rowIndex, refetch }">
        <WdsStack direction-row justify-end align-center gap-xs>
          <TrustedContactApprove
            v-if="row.viewerCanApprove"
            :trusted-contact-id="row.id"
            @approved="refetch"
            @error="handleApproveError"
          >
            <template #default="{ loading: loadingApproval }">
              <WdsButtonGhost
                :disabled="loadingApproval"
                :data-test="`tc-approve-${rowIndex}`"
                class="direct-hire-button tw-shrink-0 tw-whitespace-nowrap"
              >
                {{ $t('Approve') }}
              </WdsButtonGhost>
            </template>
          </TrustedContactApprove>
          <WdsMenu placement="bottom-end" global @menu:open="openMenu" @menu:close="closeMenu">
            <template #trigger>
              <WdsButtonGhost :data-test="`tc-settings-${rowIndex}`">
                <WdsIcon name="overflow-menu-horizontal" :size="16" />
              </WdsButtonGhost>
            </template>
            <a
              v-if="row.viewerCanOfferJob"
              target="_blank"
              :href="
                laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
                  freelancer: row.workerId,
                })
              "
              data-test="tc-offer-job"
              @click.stop
            >
              <WdsMenuOption>
                <WdsIcon :size="16" name="direct-hire" />
                {{ $t('Create hire offer') }}
              </WdsMenuOption>
            </a>

            <WdsMenuOption
              v-if="row.viewerCanReInvite"
              :data-test="`tc-invite-${rowIndex}`"
              @click="openReInviteModal(row)"
            >
              <WdsIcon :size="16" name="email" />
              {{ $t('Invite to onboard') }}
            </WdsMenuOption>

            <WdsMenuOption
              v-if="row.viewerCanUpdate"
              :data-test="`tc-edit-${rowIndex}`"
              @click="openManageModal(row)"
            >
              <WdsIcon :size="16" name="edit" />
              {{ $t('Edit contact') }}
            </WdsMenuOption>

            <a
              v-if="row.viewerCanContact"
              target="_blank"
              :href="
                laravelNamedRoute(namedRoutes.frontend.user.profile.invite, {
                  freelancer: row.workerId,
                })
              "
            >
              <WdsMenuOption :data-test="`tc-message-${rowIndex}`">
                <WdsIcon :size="16" name="chat" />
                {{ $t('Message') }}
              </WdsMenuOption>
            </a>

            <WdsMenuOption
              v-if="row.viewerCanDelete"
              theme="DESTRUCTIVE"
              :data-test="`tc-delete-${rowIndex}`"
              @click="openDeleteModal(row)"
            >
              <WdsIcon :size="16" name="trash-can" />
              {{ $t('Delete') }}
            </WdsMenuOption>
          </WdsMenu>
        </WdsStack>
      </template>

      <template #actions="{ refetch }">
        <TrustedContactInviteLink
          :show="inviteLinkModalIsOpen"
          @close="inviteLinkModalIsOpen = false"
        />
        <TrustedContactImportModal :show="importModalIsOpen" @close="closeImportModal" />
        <TrustedContactManageModal
          :show="manageModalIsOpen"
          :account-id="accountId"
          :trusted-contact-id="selectedTrustedContactId"
          data-test="trusted-contact-manage-modal"
          @cancel="closeManageModal"
          @close="closeManageModal"
          @added="refetch"
          @updated="refetch"
        />

        <TrustedContactReInviteModal
          :show="reInviteModalIsOpen"
          :trusted-contact-id="selectedTrustedContactId"
          @close="closeReInviteModal"
          @invited="refetch"
        />

        <TrustedContactDeleteModal
          :trusted-contact-id="selectedTrustedContactId"
          :show="deleteModalIsOpen"
          data-test="trusted-contact-delete-modal"
          @cancel="closeDeleteModal"
          @close="closeDeleteModal"
          @deleted="refetch"
        />
        <WdsDrawer v-model:show="detailsSidebarVisible" position="right" width="420px" padding="0">
          <WorkerDetailPanel
            :viewer-account-id="accountId"
            :worker-id="selectedTrustedContactWorkerId"
            :tab-key="selectedTab"
            :for-organisation="forOrganisation"
            @close="closeDetails"
            @updated="refetch"
            @deleted="refetch"
          />
        </WdsDrawer>
      </template>

      <template #no-trusted-contacts-found>
        <NoTrustedContactsFound>
          <template #actions>
            <WdsButton secondary data-test="invite-contact-button" @click="toggleInviteModal">
              {{ $t('Invite contact') }}
            </WdsButton>
            <WdsButton primary :href="laravelNamedRoute(namedRoutes.frontend.flows.hiring.actions)">
              {{ $t('Create a job') }}
            </WdsButton>
          </template>
        </NoTrustedContactsFound>
      </template>
    </TrustedContactsTable>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import {
  WdsMenu,
  WdsMenuOption,
  WdsStack,
  WdsIcon,
  WdsButton,
  WdsButtonGhost,
  WdsDrawer,
} from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { TrustedContactOrigin } from '@/components/Contacts/CompanyFreelancer/types'
import TrustedContactsTable from '@/components/Contacts/CompanyFreelancer/PageContacts/TrustedContactsTable.vue'
import TrustedContactInviteLink from '@/components/Contacts/CompanyFreelancer/TrustedContactInviteLink.vue'
import TrustedContactImportModal from '@/components/Contacts/CompanyFreelancer/TrustedContactImportModal.vue'
import TrustedContactManageModal from '@/components/Contacts/CompanyFreelancer/TrustedContactManageModal.vue'
import TrustedContactReInviteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactReInviteModal.vue'
import TrustedContactApprove from '@/components/Contacts/CompanyFreelancer/TrustedContactApprove.vue'
import TrustedContactDeleteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactDeleteModal.vue'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import NoTrustedContactsFound from '@/components/Contacts/CompanyFreelancer/NoTrustedContactsFound.vue'
import { NetworkError } from '@/utils/notification'
import useQueryEncoder from '@/composables/useQueryEncoder'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import PageHeader from '@/components/PageHeader.vue'

defineProps({
  totalRequestsToJoin: {
    type: [Number, String],
    required: true,
  },
})

const accountId = computed(() => currentAccount.globalId)

const inviteLinkModalIsOpen = ref(false)
const importModalIsOpen = ref(false)
const manageModalIsOpen = ref(false)
const reInviteModalIsOpen = ref(false)
const forOrganisation = ref(false)
const detailsSidebarVisible = ref(false)
const menuIsOpen = ref(false)
const deleteModalIsOpen = ref(false)
const selectedTrustedContactId = ref(null)
const selectedTrustedContactWorkerId = ref(null)
const selectedTab = ref(null)

const trustedContactId = useQueryEncoder('id')
const trustedContactWorkerId = useQueryEncoder('worker-id')
const inviteTrustedContact = useQueryEncoder('invite-trusted-contact')
const activeTab = useQueryEncoder('tab')

const settingsColumn = [
  {
    key: 'settings',
    slot: 'Settings',
    title: null,
    class: 'vmiddle',
  },
]

const openMenu = () => {
  menuIsOpen.value = true
}
const closeMenu = () => {
  menuIsOpen.value = false
}

const openManageModal = (row) => {
  selectedTrustedContactId.value = row.id
  manageModalIsOpen.value = true
  detailsSidebarVisible.value = false
}
const closeManageModal = () => {
  manageModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openDeleteModal = (row) => {
  selectedTrustedContactId.value = row.id
  deleteModalIsOpen.value = true
  detailsSidebarVisible.value = false
}
const closeDeleteModal = () => {
  deleteModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openReInviteModal = (row) => {
  reInviteModalIsOpen.value = true
  selectedTrustedContactId.value = row.id
}
const closeReInviteModal = () => {
  reInviteModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openDetails = (row) => {
  if (!menuIsOpen.value) {
    detailsSidebarVisible.value = true
    selectedTrustedContactId.value = row.id
    selectedTrustedContactWorkerId.value = row.workerId
    selectedTab.value = row.tabName
  }
}
const closeDetails = () => {
  detailsSidebarVisible.value = false
  selectedTrustedContactId.value = null
  selectedTrustedContactWorkerId.value = null
  selectedTab.value = null
  activeTab.value = null
}

const openInviteLinkModal = () => {
  inviteLinkModalIsOpen.value = true
}

const openImportModal = () => {
  importModalIsOpen.value = true
}
const closeImportModal = () => {
  importModalIsOpen.value = false
}

const toggleActiveTab = (optionKey) => {
  closeDetails()

  forOrganisation.value = optionKey === TrustedContactOrigin.ORGANISATION_CONTACTS
}

const toggleInviteModal = () => {
  closeDetails()

  manageModalIsOpen.value = !manageModalIsOpen.value
}

const filterDrawerToggled = (filtersOpened) => {
  if (filtersOpened && detailsSidebarVisible) {
    closeDetails()
  }
}

const handleApproveError = (errors) => {
  new NetworkError({
    msg: errors[0],
    errors,
  }).dispatch()
}

onMounted(() => {
  if (inviteTrustedContact.value) {
    toggleInviteModal()
  }

  if (trustedContactId.value && trustedContactWorkerId.value) {
    openDetails({
      id: trustedContactId.value,
      workerId: trustedContactWorkerId.value,
      tabName: activeTab.value,
    })
  }
})
</script>
